/**
 * Reusable editor for organization preferences that contain tokenized titles.
 *
 * 1. Read the saved preference and optional identity preferences.
 * 2. Let the user insert a known token and preview the draft.
 * 3. Save a trimmed template or unset an empty template.
 *
 * Caller-specific copy, tokens, samples, and labels stay in the owning settings page.
 */
import React, { FC, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from '@modernloop/i18n';
import { StringOrgPref } from 'src/generated/mloop-graphql';
import { useOrgPrefStringTyped, useSetOrgPrefStringTyped, useUnsetOrgPref } from 'src/hooks/api/org';
import { useApolloRefetchQuery } from 'src/hooks/useApolloQuery';
import { isValidTitleTemplate, renderTitleTemplatePreview } from './tokenizedTitleTemplate';

/** Inputs that bind the editor to one preference and one caller's vocabulary and copy. */
type Props = {
  titlePref: StringOrgPref;
  tokens: readonly string[];
  tokenLabels: Record<string, string>;
  previewSampleValues: Record<string, string>;
  description: string;
  placeholder: string;
  emptyStateInfo: string;
  emptyStateInfoOmittedEmail?: string;
  omittedEmailWarning?: string;
  omittedNameWarning?: string;
  omittedNameAndEmailWarning?: string;
  renderPreviewLabel: (preview: string) => string;
  previewFallback: string;
  saveLabel: string;
  invalidMessage: string;
  namePref?: StringOrgPref;
  emailPref?: StringOrgPref;
  disabled: boolean;
};

/** Edits and saves one typed tokenized organization preference. */
const TokenizedTitleTemplateSetting: FC<Props> = ({
  titlePref,
  tokens,
  tokenLabels,
  previewSampleValues,
  description,
  placeholder,
  emptyStateInfo,
  emptyStateInfoOmittedEmail,
  omittedEmailWarning,
  omittedNameWarning,
  omittedNameAndEmailWarning,
  renderPreviewLabel,
  previewFallback,
  saveLabel,
  invalidMessage,
  namePref,
  emailPref,
  disabled,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const refetch = useApolloRefetchQuery();
  const [savedTemplate] = useOrgPrefStringTyped(titlePref);
  // These hooks must run unconditionally to follow the rules of hooks. titlePref is a safe sentinel
  // because a template string never equals the ANONYMIZED_* or OMIT_* identity values; the result is
  // gated on whether the corresponding preference was passed.
  const [nameIdentity] = useOrgPrefStringTyped(namePref ?? titlePref);
  const [emailIdentity] = useOrgPrefStringTyped(emailPref ?? titlePref);
  const [draft, setDraft] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleSaved = async () => {
    await refetch(['OrgPrefs']);
    setDraft(null);
    enqueueSnackbar(t('hiring-dashboard-common:common.toast.changesSaved'), { variant: 'success' });
  };
  const setTemplate = useSetOrgPrefStringTyped(titlePref, handleSaved);
  const unsetTemplate = useUnsetOrgPref(titlePref, handleSaved);
  const value = draft ?? savedTemplate ?? '';
  const isEmpty = value.trim() === '';
  const isValid = isEmpty || isValidTitleTemplate(value, tokens);
  const isDirty = draft !== null && draft.trim() !== (savedTemplate ?? '').trim();
  const nameOmitted = namePref !== undefined && (nameIdentity === 'ANONYMIZED_NAME' || nameIdentity === 'OMIT_NAME');
  const emailOmitted = emailPref !== undefined && emailIdentity === 'OMIT_EMAIL';
  const nameTokenOmitted = nameOmitted && value.includes('{{CANDIDATE_NAME}}');
  const emailTokenOmitted = emailOmitted && value.includes('{{CANDIDATE_EMAIL_ADDRESS}}');
  const omittedTokenWarning =
    nameTokenOmitted && emailTokenOmitted
      ? omittedNameAndEmailWarning
      : nameTokenOmitted
        ? omittedNameWarning
        : emailTokenOmitted
          ? omittedEmailWarning
          : undefined;
  const insertToken = (token: string) => {
    const input = inputRef.current;
    const insertion = `{{${token}}}`;
    if (!input) return setDraft(value + insertion);
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? value.length;
    setDraft(value.slice(0, start) + insertion + value.slice(end));
  };
  const handleSave = async () => {
    if (value.trim() === '') await unsetTemplate();
    else await setTemplate(value.trim());
  };
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        disabled={disabled}
        value={value}
        error={!isValid}
        helperText={!isValid ? invalidMessage : undefined}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
      />
      <Stack useFlexGap gap={1} direction="row" flexWrap="wrap">
        {tokens.map((token) => (
          <Button key={token} disabled={disabled} onClick={() => insertToken(token)}>
            {tokenLabels[token]}
          </Button>
        ))}
      </Stack>
      {isEmpty && (
        <Alert severity="info">
          {emailOmitted && emptyStateInfoOmittedEmail ? emptyStateInfoOmittedEmail : emptyStateInfo}
        </Alert>
      )}
      {!isEmpty && isValid && (
        <Typography variant="body2" color="text.secondary">
          {renderPreviewLabel(
            renderTitleTemplatePreview(
              value,
              { omitName: nameOmitted, omitEmail: emailOmitted },
              tokens,
              previewSampleValues,
              previewFallback
            )
          )}
        </Typography>
      )}
      {omittedTokenWarning && <Alert severity="warning">{omittedTokenWarning}</Alert>}
      <Box>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={disabled || !isDirty || !isValid}
          onClick={handleSave}
        >
          {saveLabel}
        </Button>
      </Box>
    </Stack>
  );
};
export default TokenizedTitleTemplateSetting;
